import { cloneDeep } from 'lodash';
import { VERTICAL_HEIGHT_OF_NODE, MetricCategoryTitle, ICON_MAP, BASE_TEMPLATE } from './util';

interface EchartNode {
  x: number;
  y: number;
  name: string;
  symbol: string;
  customObj?: object;
  label?: {
    color: string;
    backgroundColor: string;
    borderColor: string;
    borderWidth: number;
  };
}

export function calculateGraphHeight(graphData) {
  const nodeCategoryMap = {};

  let maxLevel = 0;
  // find out which category contains the most number of nodes
  graphData.forEach((node) => {
    if (nodeCategoryMap[node.scope]) {
      nodeCategoryMap[node.scope]++;
    } else {
      nodeCategoryMap[node.scope] = 1;
    }
    maxLevel = Math.max(maxLevel, nodeCategoryMap[node.scope]);
  });

  // another one is for the margin
  return VERTICAL_HEIGHT_OF_NODE * (maxLevel + 1);
}

// a trick solution to add offset in edge
// https://stackoverflow.com/questions/77156911/on-graph-data-is-there-any-way-to-add-margin-between-nodes-and-links
export function buildGraphData(containerWidth, graphData) {
  const allCategoryList: Array<{
    key: string;
    nodes: any[];
  }> = [
    {
      key: MetricCategoryTitle.Experience,
      nodes: [],
    },
    {
      key: MetricCategoryTitle.Client,
      nodes: [],
    },
    {
      key: MetricCategoryTitle.Backend,
      nodes: [],
    },
  ];

  const result = cloneDeep(BASE_TEMPLATE);
  const onlyOneNode = graphData.length === 1;

  graphData.forEach((node) => {
    const curCategory = allCategoryList.find((categoryItem) => categoryItem.key === node.scope);
    if (curCategory) {
      curCategory.nodes.push(node);
    }
  });

  const categoryCount = allCategoryList.filter(
    (categoryItem) => categoryItem.nodes.length > 0,
  ).length;
  // TODO we assume there are always 3 contexts
  const everyCategoryWidth = Math.floor(containerWidth / allCategoryList.length);

  const nodeListInEcharts: Array<EchartNode> = [];
  const linksInEcharts: Array<{
    source: string;
    target: string;
    lineStyle: {
      curveness: number;
    };
  }> = [];

  // we need to calculate the maximum spans among all nodes
  let minCategoryIndex = allCategoryList.length - 1;
  let maxCategoryIndex = 0;

  allCategoryList.forEach((curCategory, categoryIndex) => {
    curCategory.nodes.forEach((node, verticalIndex) => {
      maxCategoryIndex = Math.max(categoryIndex, maxCategoryIndex);
      minCategoryIndex = Math.min(categoryIndex, minCategoryIndex);

      const x = Math.floor(categoryIndex * everyCategoryWidth);
      const y = Math.floor(verticalIndex * VERTICAL_HEIGHT_OF_NODE);
      const echartNode: EchartNode = {
        name: node.filterDisplayContent,
        x,
        y,
        symbol: ICON_MAP[node.scope] || '',
        customObj: {
          context: node.context,
        },
      };
      if (node.highLight) {
        echartNode.label = {
          color: '#f5222d',
          backgroundColor: '#fff0f0',
          borderColor: '#f5a39e',
          borderWidth: 2,
        };
      }
      nodeListInEcharts.push(echartNode);

      (node.parents || []).forEach((parentId) => {
        linksInEcharts.push({
          // source and target is the node index in the array
          source: graphData.findIndex((n) => n.id === parentId),
          target: graphData.findIndex((n) => n.id === node.id),
          // TODO add curve here
          lineStyle: {
            curveness: 0,
          },
        });
      });
    });
  });

  result.series[0].data = nodeListInEcharts;
  result.series[0].links = linksInEcharts;
  result.series[0].label.width = Math.floor(everyCategoryWidth / 4);
  // the behavior of echarts is a bit eerie
  // we need to handle the one node case specifically
  if (onlyOneNode) {
    // for one node case this config can let the node be the center bottom of the page
    // we need to relocate the node to the context it belongs to
    // result.series[0].left = 0;
    // result.series[0].top = -(containerWidth / 2);
    // result.series[0].width = containerWidth;

    const nodeContextIndex = allCategoryList.findIndex((cate) => cate.key === graphData[0].scope);
    // if there is only one node
    // here is the special logic for this case

    const initialSpanIndex = allCategoryList.length / 2;
    const targetSpans = nodeContextIndex + 0.5;
    result.series[0].left = (targetSpans - initialSpanIndex) * Math.floor(everyCategoryWidth);
    result.series[0].top = -(containerWidth / 2) + VERTICAL_HEIGHT_OF_NODE;
    // directly use the whole width
    result.series[0].width = containerWidth;
  } else {
    // set left top and width
    result.series[0].left = Math.floor(everyCategoryWidth / 2);
    result.series[0].top = VERTICAL_HEIGHT_OF_NODE;
    // the width should be using the whole width subtract the max node span count
    // it already includes the left and right margins
    result.series[0].width =
      containerWidth -
      (allCategoryList.length - (maxCategoryIndex - minCategoryIndex)) * everyCategoryWidth;
  }

  return result;
}
